import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { DayLabel, TodayBadge } from '../groupLesson/GroupLessonStyled';
import PrivateLessonPanelShell from './PrivateLessonPanelShell';
import {
  COLORS,
  EVENT_TYPE_META,
  SplitViewLayout,
  WeekGridContainer,
  WeekTimeColumn,
  WeekTimeHeaderSpacer,
  WeekTimeLabel,
  WeekDayColumn,
  WeekDayHeader,
  WeekDayBody,
  WeekEventCard,
  MIN_CARD_EXPOSED_HEIGHT,
  WeekEventContent,
  WeekEventTime,
  WeekEventLabel,
  WeekCurrentTimeLine,
  WeekPanelItemBar,
  DetailPanelHeader,
  DetailPanelTitle,
  DetailPanelCountPill,
  DetailPanelBody,
  DetailPanelFooter,
  DetailPanelFooterLink,
  DetailPanelAddButton,
  WeekHoverSlot,
  WeekPanelLoadMoreHint,
  EventTypeBadge,
  PanelRowJumpPill,
  ATTEND_STATUS_META,
  GROUP_BAR_COLOR,
  GROUP_CARD_META,
  ETC_CARD_META,
  OT_CARD_META,
  WeekEventStatusBar,
  WeekEventTrainer,
  TRAINER_PALETTE,
  PanelTrainerGroup,
  PanelTrainerGroupHeader,
  PanelTrainerDot,
  PanelTrainerName,
  PanelTrainerCount,
  PanelGroupItemRow,
  PanelItemTopLine,
  PanelConsultContent,
  PanelItemTime,
  PanelItemName,
  PanelItemCount,
} from './PrivateLessonStyled';
import { getEventTypeKey, getEventCardKey } from './hooks/usePrivateLessonData';
import { layoutDayEvents, layoutStackedOffsets } from './eventOverlapLayout';
import LessonHoverTooltip from './LessonHoverTooltip';
import useScheduleEventDrag from './hooks/useScheduleEventDrag';
import {
  getLessonAttendStatus,
  isLessonEvent,
  getEventBarColor,
  getFilteredStatusCardMeta,
  getEventDisplayName,
  isOtGroupEvent,
  getGroupMemberCount,
  GROUP_LESSON_LABEL,
} from './lessonStatus';

const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];
const SHORT_EVENT_HOURS = 0.5; // 30분 이하 이벤트는 한 줄 표기
const MINUTES_PER_HOUR = 60;
// 짧은 일정도 설정의 '카드 최소 높이(분)'만큼은 확보한다 (일간 카드와 같은 규칙).
// 시작 위치는 실제 시간 그대로라 뒤 일정과 겹칠 수 있고, 겹치면 hover 한 카드가 위로 올라온다.

const toHHmm = totalMinutes =>
  `${String(Math.floor(totalMinutes / MINUTES_PER_HOUR)).padStart(2, '0')}:${String(totalMinutes % MINUTES_PER_HOUR).padStart(2, '0')}`;


// 카드 라벨: 개인=회원명 / 듀엣=듀엣 · 김수현, 유가영 / 상담=등록(재등록) · 박은비 / 그룹=그룹 · 수업명
// 상담은 결과를 앞에 둬서 좁아질 때 이름부터 …로 잘리게 한다.
const getEventLabel = event => {
  if (event.courseClassType === 'CONSULTATION') return getEventDisplayName(event);
  if (event.courseClassType === 'ETC') return `기타 · ${event.className || ''}`;
  if (event.courseClassType === 'GROUP_LESSON') return `그룹 · ${event.className || ''}`;
  if (event.courseClassType === 'DUET_LESSON' || event.courseClassType === 'OT') {
    const names =
      event.members?.length > 0
        ? event.members.map(member => member.memberName).filter(Boolean).join(', ')
        : event.memberName || '';
    if (isOtGroupEvent(event)) {
      const count =
        (event.reservedMemberCount || 0) + (event.attendedMemberCount || 0) + (event.absentedMemberCount || 0);
      return `OT ${count}/${event.membersMaxCount} · ${names}`;
    }
    return `${event.courseClassType === 'OT' ? 'OT' : '듀엣'} · ${names}`;
  }
  return event.memberName || '';
};

// 지난 수업/상담 판정 (기존 EventCard와 동일 기준)
const isPastEvent = (event, now) =>
  now.isAfter(moment(`${event.registrationStartDate} ${event.endTime}`, 'YYYY-MM-DD HH:mm'));

/**
 * 개인 스케줄 주간 보기 (분할형)
 * - 좌: 주간 타임 그리드 — 전체 임직원 표시, 임직원 필터 선택 시 해당 인원만 (요일 열 클릭 = 선택일 변경)
 * - 우: 선택일 상세 패널 (400px)
 */
const PrivateLessonWeekView = ({
  weekDates,
  events,
  staffList,
  selectedTrainerIds = [],
  periodTimes,
  selectedDate,
  onSelectDate,
  hourHeight = 52, // 1시간 픽셀 (줌 상태)
  timeUnitMinutes = 60, // 표 칸 단위(분) — 지점 스케줄 설정(30/60/120). 시간→px 는 칸과 무관하게 hourHeight 기준
  snapMinutes = 60, // 선택 단위(분) — 빈칸 hover·클릭·우클릭·드롭 스냅 간격. 표 칸과 독립
  cardMinMinutes = 48, // 짧은 일정 카드의 최소 높이(분). 기본 48분 = 1시간의 80%
  panelWidth,
  panelCollapsed,
  onTogglePanel,
  onPanelWidthChange,
  onEventClick,
  onGoToDay,
  onAddSchedule,
  onEmptyCellClick,
  onEmptyCellContextMenu,
  onEventContextMenu,
  onEventDrop,
  canDragEvent = true,
  onGroupJump,
  ticketAlertCount,
  ticketAlertCountUnit,
  typeFilter,
}) => {
  const openStart = periodTimes?.startTime != null ? Number(periodTimes.startTime) : 8;
  const openEnd = periodTimes?.endTime != null ? Number(periodTimes.endTime) : 22;
  const hourCount = Math.max(openEnd - openStart, 1);
  const bodyHeight = hourCount * hourHeight;
  // 선택 구간 = 선택 단위. 1시간이면 구간 하나가 1시간(기존과 동일)이다. 구간 위치는 자정부터의 분으로 다룬다.
  const snapHeight = (hourHeight * snapMinutes) / MINUTES_PER_HOUR;
  const openStartMinutes = openStart * MINUTES_PER_HOUR;
  const openEndMinutes = openStartMinutes + hourCount * MINUTES_PER_HOUR;
  const minutesToTop = minutes => ((minutes - openStartMinutes) / MINUTES_PER_HOUR) * hourHeight;
  // 구간 박스 높이 — 본문 끝을 넘지 않게 자른다
  const snapBoxHeight = minutes => (Math.min(snapMinutes, openEndMinutes - minutes) / MINUTES_PER_HOUR) * hourHeight;

  // 왼쪽 시간 줄 — 표 칸 단위마다 라벨. 범위가 칸으로 안 나눠지면(2시간 칸) 마지막 칸은 남은 길이만큼 높이를 준다.
  const hourLabels = useMemo(() => {
    const labels = [];
    for (let minutes = openStartMinutes; minutes < openEndMinutes; minutes += timeUnitMinutes) {
      labels.push({
        label: toHHmm(minutes),
        height: (Math.min(timeUnitMinutes, openEndMinutes - minutes) / MINUTES_PER_HOUR) * hourHeight,
      });
    }
    return labels;
  }, [openStartMinutes, openEndMinutes, timeUnitMinutes, hourHeight]);

  // 본문 안 세로 위치(px) → 그 위치가 속한 선택 구간의 시작 시각(분)
  const slotMinutesAtOffset = offsetY => openStartMinutes + Math.floor(offsetY / snapHeight) * snapMinutes;

  // 표시 대상 이벤트 (그룹레슨 포함 — 담당 강사 기준, 임직원 필터 적용), 날짜별 그룹핑 + 시간순 정렬
  const eventsByDate = useMemo(() => {
    const byDate = {};
    events.forEach(event => {
      if (selectedTrainerIds.length > 0 && !selectedTrainerIds.includes(event.trainerId)) return;
      const key = event.registrationStartDate;
      if (!byDate[key]) byDate[key] = [];
      byDate[key].push(event);
    });
    Object.values(byDate).forEach(list =>
      list.sort((a, b) => (a.startTime || '').localeCompare(b.startTime || '')),
    );
    return byDate;
  }, [events, selectedTrainerIds]);

  const now = moment();
  const nowIndex = now.hour() + now.minute() / 60;

  const selectedEvents = eventsByDate[selectedDate.format('YYYY-MM-DD')] || [];

  // 선택일 이벤트를 임직원별 그룹으로 (월간 패널과 동일 구조 — 통합 임직원 목록 순서 = 색 팔레트 순서)
  const trainerGroups = useMemo(() => {
    const byTrainer = new Map();
    selectedEvents.forEach(event => {
      if (!byTrainer.has(event.trainerId)) byTrainer.set(event.trainerId, []);
      byTrainer.get(event.trainerId).push(event);
    });

    const groups = [];
    (staffList || []).forEach((staff, index) => {
      const items = byTrainer.get(staff.trainerId);
      if (items) {
        groups.push({
          trainerId: staff.trainerId,
          trainerName: staff.trainerName,
          color: TRAINER_PALETTE[index % TRAINER_PALETTE.length],
          items,
        });
        byTrainer.delete(staff.trainerId);
      }
    });
    // 통합 목록에 없는 담당자(퇴사자 등)의 이벤트도 뒤에 표시
    byTrainer.forEach((items, trainerId) => {
      groups.push({
        trainerId,
        trainerName: items[0]?.trainerName || '-',
        color: TRAINER_PALETTE[groups.length % TRAINER_PALETTE.length],
        items,
      });
    });
    return groups;
  }, [selectedEvents, staffList]);

  // 패널 목록 무한 스크롤 — 선택일이 바뀌거나 이벤트 수가 바뀌면 노출 개수 초기화
  const [visibleCount, setVisibleCount] = useState(20);
  useEffect(() => {
    setVisibleCount(20);
  }, [selectedDate, selectedEvents.length]);

  const handlePanelScroll = e => {
    const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight - 80) {
      setVisibleCount(count => Math.min(count + 20, selectedEvents.length));
    }
  };

  // 패널 높이를 좌측 주간 그리드에 맞춤 — 패널이 그리드보다 길어지면 내부 스크롤이 생기지 않아
  // 스크롤 기반 무한 로드가 발동하지 못하므로, 그리드 높이를 측정해 maxHeight로 제한한다
  const gridRef = useRef(null);
  const [gridHeight, setGridHeight] = useState(null);
  useEffect(() => {
    if (!gridRef.current) return undefined;
    const observer = new ResizeObserver(entries => {
      const height = entries[0]?.contentRect?.height;
      if (height) setGridHeight(Math.round(height));
    });
    observer.observe(gridRef.current);
    return () => observer.disconnect();
  }, []);

  // 내용이 패널을 다 못 채우면(스크롤 불가) 스크롤 없이도 다음 20건을 자동 로드
  const panelBodyRef = useRef(null);
  useEffect(() => {
    const body = panelBodyRef.current;
    if (!body) return;
    if (visibleCount < selectedEvents.length && body.scrollHeight <= body.clientHeight) {
      setVisibleCount(count => Math.min(count + 20, selectedEvents.length));
    }
  }, [visibleCount, selectedEvents.length, gridHeight]);

  // visibleCount 예산만큼 그룹 순서대로 항목을 잘라 표시 (월간 패널과 동일)
  const visibleTrainerGroups = useMemo(() => {
    let budget = visibleCount;
    const result = [];
    for (const group of trainerGroups) {
      if (budget <= 0) break;
      const items = group.items.slice(0, budget);
      budget -= items.length;
      result.push({ ...group, items, totalCount: group.items.length });
    }
    return result;
  }, [trainerGroups, visibleCount]);

  // 빈칸 호버 미리보기 — 그룹레슨 EventCell 호버 박스와 동일한 시각 (dateKey + 칸 시작 분 단위)
  const [hoverSlot, setHoverSlot] = useState(null);
  // 펼친 카드 — { el, rect }. rect 는 펼치기 전 원래 자리(겹쳐서 좁아진 크기, 뷰포트 좌표). 마우스가 이 밖으로 나가면 접는다.
  // 펼친 폭 전체를 hover 영역으로 쓰면 옆 카드로 옮겨 가기 어렵다(사용자 요청 2026-09-29).
  // 카드를 드나들 때마다 주간 뷰 전체가 다시 그려지지 않도록 state 대신 ref + 클래스 토글로 다룬다.
  const expandedCardRef = useRef(null);
  const collapseExpandedCard = () => {
    expandedCardRef.current?.el.classList.remove('is-expanded');
    expandedCardRef.current = null;
  };
  // rect 는 뷰포트 좌표라 스크롤·리사이즈로 어긋난다 — 그때는 접는다
  useEffect(() => {
    const collapse = () => {
      expandedCardRef.current?.el.classList.remove('is-expanded');
      expandedCardRef.current = null;
    };
    window.addEventListener('scroll', collapse, true);
    window.addEventListener('resize', collapse);
    return () => {
      window.removeEventListener('scroll', collapse, true);
      window.removeEventListener('resize', collapse);
    };
  }, []);

  // 카드 드래그 이동 — 놓을 칸 하이라이트(dropSlot)는 빈칸 hover 미리보기와 같은 모양을 쓴다
  const [dropSlot, setDropSlot] = useState(null);
  const { dragRef, dragStartYRef, dragEvent, endDrag, getDragProps } = useScheduleEventDrag({
    enabled: canDragEvent,
    onStart: () => {
      collapseExpandedCard();
      setHoverSlot(null);
    },
  });

  // 놓을 구간 = 원래 시작 시각이 속한 선택 구간 + 세로 이동량(선택 단위 반올림). 반환은 구간 시작 시각(분).
  // 카드가 그려진 위치(겹침으로 밀린 오프셋, 열림 시각 앞당김)와 무관하게 제자리에 놓으면 원래 시각이다.
  // 본문 범위를 넘으면 처음·끝 구간으로 당긴다.
  const getDropMinutes = e => {
    const dragged = dragRef.current;
    if (!dragged) return null;
    const originSlot =
      Math.floor((dragged.startHour * MINUTES_PER_HOUR + dragged.startMinute) / snapMinutes) * snapMinutes;
    const minutes = originSlot + Math.round((e.clientY - dragStartYRef.current) / snapHeight) * snapMinutes;
    return Math.min(Math.max(minutes, openStartMinutes), openEndMinutes - snapMinutes);
  };

  const handleBodyDragOver = (e, dateKey) => {
    if (!dragRef.current) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const minutes = getDropMinutes(e);
    if (minutes === null) {
      setDropSlot(null);
      return;
    }
    setDropSlot(prev => (prev?.dateKey === dateKey && prev?.minutes === minutes ? prev : { dateKey, minutes }));
  };

  const handleBodyDragLeave = e => {
    if (!e.currentTarget.contains(e.relatedTarget)) setDropSlot(null);
  };

  const handleBodyDrop = (e, date) => {
    e.preventDefault();
    const dropped = dragRef.current;
    const minutes = getDropMinutes(e);
    endDrag();
    setDropSlot(null);
    if (dropped && minutes !== null) onEventDrop?.(dropped, date, minutes);
  };

  const handleBodyMouseMove = (e, dateKey) => {
    // 이벤트 카드 위에서는 미리보기 숨김 (그룹레슨 :not(:has(.event-card:hover)) 동작과 동일)
    if (e.target.closest('.week-event-card')) {
      setHoverSlot(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const minutes = slotMinutesAtOffset(e.clientY - rect.top);
    if (minutes < openStartMinutes || minutes >= openEndMinutes) {
      setHoverSlot(null);
      return;
    }
    setHoverSlot(prev => (prev?.dateKey === dateKey && prev?.minutes === minutes ? prev : { dateKey, minutes }));
  };

  // 하루치 이벤트 카드 렌더링 — 시간이 겹치는 이벤트는 클러스터별 컬럼으로 좌우 분할
  const renderDayEvents = dayEvents => {
    const overlapLayout = layoutDayEvents(dayEvents);
    // 뒤 카드가 앞 카드의 첫 줄까지 덮지 않도록 아래로 밀어낼 픽셀
    const stackOffsets = layoutStackedOffsets(dayEvents, hourHeight, MIN_CARD_EXPOSED_HEIGHT);

    return dayEvents.map((event, index) => {
      const startIndex = event.startHour + event.startMinute / 60;
      const endIndex = event.endHour + event.endMinute / 60;
      if (endIndex <= openStart || startIndex >= openEnd) return null;

      const clampedStart = Math.max(startIndex, openStart);
      const clampedEnd = Math.min(endIndex, openEnd);
      const isPast = isPastEvent(event, now);
      const isConsult = event.courseClassType === 'CONSULTATION';
      const isEtc = event.courseClassType === 'ETC';
      const isGroup = event.courseClassType === 'GROUP_LESSON';
      const isLesson = isLessonEvent(event);
      // 개인·듀엣은 출석 상태색 (지난 수업도 상태색 유지 — 시안 확정), 그룹/상담은 유형색
      const statusMeta = isLesson ? ATTEND_STATUS_META[getLessonAttendStatus(event)] : null;
      // 그룹·상담을 단독으로 보는 중이면 카운트 칩과 같은 상태색으로 칠한다 (그룹=예약 상태, 상담=결과)
      const filteredMeta = getFilteredStatusCardMeta(event, typeFilter);
      const bgColor = filteredMeta
        ? filteredMeta.weekBg
        : isGroup
          ? EVENT_TYPE_META.group.cardBg
          : isConsult
            ? isPast
              ? COLORS.consultationPast
              : COLORS.consultationBg
            : isEtc
              ? ETC_CARD_META.dayBg
              : event.courseClassType === 'OT'
                ? OT_CARD_META.weekBg
                : statusMeta.weekBg;
      const barColor =
        isGroup ? filteredMeta?.barColor || GROUP_BAR_COLOR : isConsult || isEtc ? null : (filteredMeta || statusMeta).barColor;
      const borderColor = isConsult
        ? filteredMeta?.dayBorder || COLORS.consultationBorder
        : isEtc
          ? ETC_CARD_META.dayBorder
          : '#E4E9EA';
      // 밝은 배경(그룹·개인·듀엣) 카드는 어두운 글씨, 진한 배경(상담)은 기본 흰색 유지
      const fontColor = filteredMeta
        ? filteredMeta.weekFont
        : isGroup
          ? GROUP_CARD_META.weekFont
          : isConsult || isEtc
            ? null
            : statusMeta.weekFont;
      const label = getEventLabel(event);
      const isShort = endIndex - startIndex <= SHORT_EVENT_HOURS;

      // 겹침 클러스터 컬럼 분할 (수업·상담 공통)
      let splitProps = { cardLeft: '3px', cardRight: '3px' };
      const { col, cols } = overlapLayout.get(event) || { col: 0, cols: 1 };
      const isNarrow = cols > 1; // 분할 카드는 폭이 좁아 시간·상태 텍스트를 접는다
      if (cols > 1) {
        splitProps = {
          cardLeft: `${(col * 100) / cols}%`,
          cardWidth: `${100 / cols}%`,
          cardZIndex: 2 + col,
        };
      }

      const cardKey = getEventCardKey(event, `week-event-${index}`);

      return (
        <LessonHoverTooltip
          key={cardKey}
          event={event}
          ticketAlertCount={ticketAlertCount}
          ticketAlertCountUnit={ticketAlertCountUnit}>
          <WeekEventCard
            className="week-event-card"
            onMouseEnter={e => {
              collapseExpandedCard();
              const el = e.currentTarget;
              expandedCardRef.current = { el, rect: el.getBoundingClientRect() };
              el.classList.add('is-expanded');
            }}
            onMouseMove={e => {
              const current = expandedCardRef.current;
              if (current?.el !== e.currentTarget) return;
              const { rect } = current;
              if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) {
                collapseExpandedCard();
              }
            }}
            onMouseLeave={e => {
              if (expandedCardRef.current?.el === e.currentTarget) collapseExpandedCard();
            }}
            data-class-time-id={isConsult ? undefined : event.courseClassTimeId}
            data-course-class-id={isConsult ? undefined : event.courseClassId}
            data-consultation-id={isConsult ? event.consultationId : undefined}
            cardTop={(clampedStart - openStart) * hourHeight + (stackOffsets.get(event) || 0)}
            cardHeight={Math.max(
              (clampedEnd - clampedStart) * hourHeight,
              (hourHeight * cardMinMinutes) / MINUTES_PER_HOUR,
            )}
            bgColor={bgColor}
            borderColor={borderColor}
            style={{
              ...(barColor ? { paddingLeft: '11px' } : null),
              ...(dragEvent === event ? { opacity: 0.4 } : null),
            }}
            {...splitProps}
            {...getDragProps(event)}
            onClick={e => onEventClick(e, event, index)}
            onContextMenu={e => onEventContextMenu?.(e, event)}>
            {barColor && <WeekEventStatusBar barColor={barColor} />}
            <WeekEventContent>
              {/* 주간은 강사 컬럼이 없어 강사명이 가장 중요 — 겹쳐 좁아지면 시간을 접고 강사명을 우선 노출 */}
              <WeekEventTime fontColor={fontColor}>
                {event.trainerName && <WeekEventTrainer fontColor={fontColor}>{event.trainerName}</WeekEventTrainer>}
                {!isNarrow && event.startTime}
                {!isNarrow && isShort && label ? ` ${label}` : ''}
              </WeekEventTime>
              {!isShort && <WeekEventLabel fontColor={fontColor}>{label}</WeekEventLabel>}
            </WeekEventContent>
          </WeekEventCard>
        </LessonHoverTooltip>
      );
    });
  };

  // 기간 조회로 7일을 넘으면 가로 스크롤 + 컬럼 최소폭 고정 (시간 컬럼은 sticky로 남긴다)
  const isExtended = weekDates.length > 7;

  return (
    <SplitViewLayout>
      {/* 좌: 주간 타임 그리드 (기간 조회 시 일수만큼 옆으로 늘어난다) */}
      <WeekGridContainer ref={gridRef} sx={isExtended ? { overflowX: 'auto' } : undefined}>
        <WeekTimeColumn
          sx={isExtended ? { position: 'sticky', left: 0, zIndex: 3, backgroundColor: '#fff' } : undefined}>
          <WeekTimeHeaderSpacer />
          {hourLabels.map(({ label, height }) => (
            <WeekTimeLabel key={label} labelHeight={height}>
              {label}
            </WeekTimeLabel>
          ))}
        </WeekTimeColumn>

        {weekDates.map(date => {
          const isToday = date.isSame(now, 'day');
          const isSelected = date.isSame(selectedDate, 'day');
          const dayEvents = eventsByDate[date.format('YYYY-MM-DD')] || [];
          // 요일은 인덱스가 아니라 실제 날짜에서 — 기간 조회는 일요일 시작이 아닐 수 있다
          const dow = date.day();

          return (
            <WeekDayColumn
              key={date.format('YYYY-MM-DD')}
              isToday={isToday}
              isSelected={isSelected}
              sx={isExtended ? { minWidth: '120px', flex: '0 0 120px' } : undefined}
              onClick={() => onSelectDate(date.clone())}>
              <WeekDayHeader isToday={isToday}>
                <DayLabel isWeekend={dow === 0 || dow === 6} isSunday={dow === 0}>
                  {date.date()} ({DAY_NAMES[dow]})
                  {isToday && <TodayBadge>today</TodayBadge>}
                </DayLabel>
              </WeekDayHeader>

              <WeekDayBody
                bodyHeight={bodyHeight}
                hourHeight={hourHeight}
                unitMinutes={timeUnitMinutes}
                onClick={e => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  onEmptyCellClick(date, slotMinutesAtOffset(e.clientY - rect.top));
                }}
                onContextMenu={e => {
                  // 카드 위 우클릭은 카드가 이미 처리했다(복사하기). 빈 곳만 붙여넣기로 받는다.
                  if (e.target.closest('.week-event-card')) return;
                  const rect = e.currentTarget.getBoundingClientRect();
                  onEmptyCellContextMenu?.(e, date, slotMinutesAtOffset(e.clientY - rect.top));
                }}
                onMouseMove={e => handleBodyMouseMove(e, date.format('YYYY-MM-DD'))}
                onMouseLeave={() => setHoverSlot(null)}
                onDragOver={e => handleBodyDragOver(e, date.format('YYYY-MM-DD'))}
                onDragLeave={handleBodyDragLeave}
                onDrop={e => handleBodyDrop(e, date)}>
                {renderDayEvents(dayEvents)}
                {dropSlot?.dateKey === date.format('YYYY-MM-DD') && (
                  <WeekHoverSlot slotTop={minutesToTop(dropSlot.minutes)} slotHeight={snapBoxHeight(dropSlot.minutes)} />
                )}
                {hoverSlot?.dateKey === date.format('YYYY-MM-DD') && (
                  <WeekHoverSlot slotTop={minutesToTop(hoverSlot.minutes)} slotHeight={snapBoxHeight(hoverSlot.minutes)} />
                )}
                {isToday && nowIndex >= openStart && nowIndex <= openEnd && (
                  <WeekCurrentTimeLine topPosition={(nowIndex - openStart) * hourHeight} />
                )}
              </WeekDayBody>
            </WeekDayColumn>
          );
        })}
      </WeekGridContainer>

      {/* 우: 선택일 상세 패널 (접기/가로폭 조절) */}
      <PrivateLessonPanelShell
        width={panelWidth || 400}
        collapsed={panelCollapsed}
        onToggleCollapse={onTogglePanel}
        onWidthChange={onPanelWidthChange}
        count={selectedEvents.length}
        maxHeight={gridHeight}>
        <DetailPanelAddButton onClick={onAddSchedule}>+ 일정 추가</DetailPanelAddButton>
        <DetailPanelHeader>
          <DetailPanelTitle>
            {selectedDate.format('M월 D일')} ({DAY_NAMES[selectedDate.day()]})
          </DetailPanelTitle>
          <DetailPanelCountPill>{selectedEvents.length}건</DetailPanelCountPill>
        </DetailPanelHeader>

        <DetailPanelBody ref={panelBodyRef} onScroll={handlePanelScroll}>
          {visibleTrainerGroups.map(group => (
            <PanelTrainerGroup key={group.trainerId}>
              <PanelTrainerGroupHeader>
                <PanelTrainerDot dotColor={group.color} />
                <PanelTrainerName>{group.trainerName} 강사</PanelTrainerName>
                <PanelTrainerCount>{group.totalCount}건</PanelTrainerCount>
              </PanelTrainerGroupHeader>

              {group.items.map((event, index) => {
                const typeMeta = EVENT_TYPE_META[getEventTypeKey(event.courseClassType)];
                const isGroup = event.courseClassType === 'GROUP_LESSON';
                const isConsult = event.courseClassType === 'CONSULTATION';
                const memberCount = getGroupMemberCount(event);
                // 기타는 내용 원문이 목록에 실려 온다 (상담은 200자 절단본)
                const consultContent = isConsult
                  ? event.consultContentPreview
                  : event.courseClassType === 'ETC'
                    ? event.memo
                    : '';
                return (
                  <LessonHoverTooltip
                    key={getEventCardKey(event, `panel-${index}`)}
                    event={event}
                    ticketAlertCount={ticketAlertCount}
                    ticketAlertCountUnit={ticketAlertCountUnit}
                    placement="left">
                    <PanelGroupItemRow onClick={e => (isGroup ? onGroupJump(event) : onEventClick(e, event, index))}>
                      <PanelItemTopLine>
                        <WeekPanelItemBar barColor={getEventBarColor(event, typeFilter)} />
                        <PanelItemTime>{event.startTime}</PanelItemTime>
                        <PanelItemName>
                          {memberCount ? GROUP_LESSON_LABEL : getEventDisplayName(event)}
                          {memberCount && <PanelItemCount>{memberCount}</PanelItemCount>}
                        </PanelItemName>
                        <EventTypeBadge badgeColor={typeMeta.color} badgeBg={typeMeta.badgeBg}>
                          {typeMeta.label}
                        </EventTypeBadge>
                      </PanelItemTopLine>
                      {consultContent && <PanelConsultContent>{consultContent}</PanelConsultContent>}
                      {isGroup && (
                        <PanelRowJumpPill
                          className="panel-jump-pill"
                          onClick={e => {
                            e.stopPropagation();
                            onGroupJump(event);
                          }}>
                          수업으로 이동 ›
                        </PanelRowJumpPill>
                      )}
                    </PanelGroupItemRow>
                  </LessonHoverTooltip>
                );
              })}
            </PanelTrainerGroup>
          ))}
          {visibleCount < selectedEvents.length && (
            <WeekPanelLoadMoreHint>스크롤하여 더 보기 · 남은 {selectedEvents.length - visibleCount}건</WeekPanelLoadMoreHint>
          )}
        </DetailPanelBody>

        <DetailPanelFooter>
          <DetailPanelFooterLink onClick={onGoToDay}>일(직원별) 보기로 이동 →</DetailPanelFooterLink>
        </DetailPanelFooter>
      </PrivateLessonPanelShell>
    </SplitViewLayout>
  );
};

export default PrivateLessonWeekView;
